/**
 * Las primitivas de interfaz.
 *
 * Envuelven las clases del sistema de diseño del prototipo (`styles/base.css`),
 * que es el diseño ya aprobado. Ningún componente de página escribe una clase
 * `.badge--warn` a mano: pide un `<StatusBadge>` y el tono lo decide el mapa de
 * `@cleanblue/shared`, que es el mismo que usa el resto del sistema.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Icon, isIconName, type IconName } from '@/components/common/NavIcons';
import { createPortal } from 'react-dom';
import { formatMoney, type Tone } from '@cleanblue/shared';

/* ── Dinero ───────────────────────────────────────────────────────────────── */

/**
 * El dinero llega del servidor como **texto de céntimos** y acá solo se
 * formatea. No se suma, no se multiplica, no se compara: si una pantalla
 * necesita un total, lo calcula el Worker.
 */
export function Money({ value, size, dim }: {
  value: string | number | null | undefined;
  size?: 'lg' | 'xl';
  dim?: boolean;
}) {
  if (value == null) return <span className="money money--dim">—</span>;
  const minor = typeof value === 'number' ? value : Number.parseInt(value, 10);
  const cls = ['money', size ? `money--${size}` : '', dim ? 'money--dim' : ''].filter(Boolean).join(' ');
  return <span className={cls}>{formatMoney(minor)}</span>;
}

/* ── Estado ───────────────────────────────────────────────────────────────── */

const TONE_CLASS: Record<Tone, string> = {
  ok: 'badge--ok',
  warn: 'badge--warn',
  danger: 'badge--danger',
  info: 'badge--info',
  neutral: 'badge--muted',
};

export function Badge({ tone = 'neutral', plain, children, title }: {
  tone?: Tone; plain?: boolean; children: ReactNode; title?: string;
}) {
  return (
    <span className={`badge ${TONE_CLASS[tone]}${plain ? ' badge--plain' : ''}`} title={title}>
      {children}
    </span>
  );
}

/** Traduce un valor del dominio a su etiqueta y su tono, en un solo lugar. */
export function StatusBadge<T extends string>({ value, labels, tones, hints }: {
  value: T;
  labels: Record<T, string>;
  tones: Record<T, Tone>;
  hints?: Record<T, string>;
}) {
  return <Badge tone={tones[value]} title={hints?.[value]}>{labels[value]}</Badge>;
}

/* ── Avatar ───────────────────────────────────────────────────────────────── */

/**
 * Foto de perfil, con las iniciales como fallback.
 *
 * `src` es la URL privada del Worker (`/api/admin/.../photo`) — la sesión
 * viaja en cookie, así que un `<img>` de mismo origen la trae sola, sin
 * `fetch` aparte. Si la imagen falla (404, sin foto todavía, o el archivo
 * desapareció) se cae a las iniciales en vez de mostrar el ícono roto del
 * navegador.
 */
export function Avatar({ src, name, size = 28, lazy }: {
  src?: string | null; name: string; size?: number;
  /** Difiere la descarga hasta que el avatar se acerca al viewport. Es para las
   *  galerías: 84 doctores en una grilla serían 84 peticiones al montar. En una
   *  fila de tabla o en un modal no hace falta, así que no es el default. */
  lazy?: boolean;
}) {
  const [broken, setBroken] = useState(false);
  /* Se reintenta cuando cambia la foto. Sin esto, un avatar que falló una vez
     quedaba en iniciales para siempre: al subir una foto nueva, el primer
     render corre una carrera contra R2, y si pierde, la tarjeta de «Mi perfil»
     y la de la barra de arriba no se desmontan nunca — así que el usuario
     acababa de subir su foto y no la veía hasta recargar la página. */
  useEffect(() => setBroken(false), [src]);
  const initials = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]!.toUpperCase()).join('') || '?';
  const dim = { width: size, height: size, minWidth: size };

  if (src && !broken) {
    return (
      <img
        className="avatar-img"
        src={src}
        alt=""
        /* `width`/`height` como atributos además del style: reservan la caja
           antes de que llegue el archivo, que es lo que evita el salto de
           layout en una grilla donde la mayoría cae a iniciales. */
        width={size}
        height={size}
        loading={lazy ? 'lazy' : undefined}
        style={dim}
        onError={() => setBroken(true)}
      />
    );
  }
  return <span className="avatar" style={{ ...dim, fontSize: Math.round(size * 0.4) }}>{initials}</span>;
}

/* ── Superficies ──────────────────────────────────────────────────────────── */

export function Card({ title, actions, children, flush, footer }: {
  title?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean; footer?: ReactNode;
}) {
  return (
    <section className={`card${flush ? ' card--flush' : ''}`}>
      {(title || actions) && (
        <header className="card__head">
          {typeof title === 'string' ? <h2>{title}</h2> : title}
          <div className="spacer" />
          {actions}
        </header>
      )}
      <div className="card__body">{children}</div>
      {footer && <footer className="card__foot">{footer}</footer>}
    </section>
  );
}

export function PageHead({ title, subtitle, actions }: {
  title: string; subtitle?: ReactNode; actions?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div className="page-head__text">
        <h1>{title}</h1>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {actions && <div className="page-head__actions">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, meta, accent, icon }: {
  label: string; value: ReactNode; meta?: ReactNode; accent?: boolean;
  /** El ícono del recuadro, de la familia de siempre. Opcional: los recuadros
   *  que ya existen en otras pantallas siguen viéndose igual sin tocarlos. */
  icon?: IconName;
}) {
  return (
    <div className={`stat${accent ? ' stat--accent' : ''}`}>
      <div className="stat__top">
        <div className="stat__label">{label}</div>
        {/* Decorativo: el rótulo de al lado ya dice qué es, y un lector de
            pantalla que además leyera el ícono diría la misma cosa dos veces. */}
        {icon && <span className="stat__ico" aria-hidden><Icon name={icon} size={18} /></span>}
      </div>
      <div className="stat__value">{value}</div>
      {meta && <div className="stat__meta">{meta}</div>}
    </div>
  );
}

/* El ícono de cada tono sale de la familia `NavIcons`, no de un glifo suelto
   (`i`, `✓`, `!`): los glifos se veían distintos según la fuente del teléfono
   y `✓` se emojificaba en iOS. Peligro lleva el mismo ícono que Incidencias
   —es el mismo concepto: algo que salió mal— y ok el de Confirmar. */
const NOTE_ICON: Record<'info' | 'ok' | 'warn' | 'danger', IconName> = {
  info: 'info', ok: 'confirmar', warn: 'alerta', danger: 'incidencias',
};

export function Note({ tone = 'info', icon, children }: {
  tone?: 'info' | 'ok' | 'warn' | 'danger';
  /** Un nombre de la familia de íconos, o un glifo suelto (compatibilidad). */
  icon?: string; children: ReactNode;
}) {
  const name: IconName | null = icon == null ? NOTE_ICON[tone] : isIconName(icon) ? icon : null;
  return (
    <div className={`note note--${tone}`}>
      <span className="note__ico">{name ? <Icon name={name} size={16} /> : icon}</span>
      <div>{children}</div>
    </div>
  );
}

/* ── Estados de la pantalla ───────────────────────────────────────────────── */

/**
 * El vacío no es un error, y decirlo importa: «todavía no hay comprobantes por
 * revisar» tranquiliza; una tabla en blanco preocupa.
 */
export function Empty({ icon = '·', title, hint, action }: {
  /** Un nombre de la familia de íconos, o un glifo suelto. Los glifos Unicode
   *  eran el default histórico y en el teléfono varios se volvían emoji a todo
   *  color (☺ ★ ✉…); con un nombre («cobros», «doctor») se pinta el SVG de la
   *  familia, que se ve igual en todos lados. */
  icon?: string; title: string; hint?: ReactNode; action?: ReactNode;
}) {
  // Sin estilos inline: las clases `.empty__*` viven en base.css junto a
  // `.empty`, así que el vacío se ve igual en las tres carcasas y se ajusta
  // en un solo lugar.
  return (
    <div className="empty">
      <div className="empty__ico">
        {isIconName(icon) ? <Icon name={icon} size={26} /> : icon}
      </div>
      <div className="empty__title">{title}</div>
      {hint && <p className="empty__hint">{hint}</p>}
      {action && <div className="empty__action">{action}</div>}
    </div>
  );
}

export function Loading({ label = 'Cargando…' }: { label?: string }) {
  // `Icon` no acepta className: el giro lo pone el `<span>` de afuera.
  // `role="status"` para que el lector de pantalla anuncie que se está
  // esperando, sin que cada pantalla tenga que acordarse.
  return (
    <div className="empty" role="status">
      <div className="empty__ico"><span className="ico-girando"><Icon name="cargando" size={26} /></span></div>
      <div>{label}</div>
    </div>
  );
}

/* ── Esqueletos ───────────────────────────────────────────────────────────── */

/**
 * La forma de una tabla mientras carga, en vez de la caja centrada de
 * `Loading` (Fase 2, auditoría 1-sep-2026: «cargar y estar vacío se ven
 * igual»). Auto-contenido — trae su propio `.table-wrap`/`.tbl`, cabecera
 * incluida — así el llamador no repite el `head` real dos veces.
 */
export function TableSkeleton({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="table-wrap" aria-hidden="true">
      <table className="tbl">
        <thead>
          <tr>
            {Array.from({ length: cols }, (_, i) => (
              <th key={i}><span className="skeleton" style={{ width: '60%', height: 9 }} /></th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, r) => (
            <tr key={r}>
              {Array.from({ length: cols }, (_, c) => (
                // El ancho varía por columna, no por fila: así el conjunto lee
                // como una tabla real (una columna angosta, otra ancha) y no
                // como rayas idénticas repetidas.
                <td key={c}><span className="skeleton" style={{ width: `${[85, 55, 70, 40][c % 4]}%` }} /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** La forma de una tarjeta mientras carga: un título y unas líneas de cuerpo
 *  de ancho decreciente, como texto de verdad interrumpido a mitad. */
export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  const anchos = [92, 78, 60, 88, 45];
  return (
    <div className="card" aria-hidden="true">
      <div className="card__body" style={{ display: 'grid', gap: 10 }}>
        <span className="skeleton" style={{ width: '40%', height: 14 }} />
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} className="skeleton" style={{ width: `${anchos[i % anchos.length]}%` }} />
        ))}
      </div>
    </div>
  );
}

/** El error va en la misma caja centrada que el vacío y la carga: los tres son
 *  «todavía no hay tabla», y saltar de una caja centrada a una franja roja
 *  pegada arriba hacía que el error pareciera de otra pantalla. */
export function ErrorState({ error, retry }: { error: { message: string }; retry?: () => void }) {
  return (
    <div className="empty empty--error" role="alert">
      <Note tone="danger">
        {error.message}
        {retry && (
          <>
            {' '}
            <button type="button" className="btn btn--ghost btn--sm" onClick={retry}>Reintentar</button>
          </>
        )}
      </Note>
    </div>
  );
}

/**
 * El envoltorio de carga: un solo lugar decide qué se ve mientras se espera,
 * qué se ve si falla y qué se ve si no hay nada.
 */
export function Async<T>({ query, empty, skeleton, children }: {
  query: { data?: T; isLoading: boolean; error: { message: string } | null; refetch: () => void };
  empty?: (data: T) => ReactNode;
  /** La forma de lo que va a aparecer (`TableSkeleton`, `CardSkeleton`), en
   *  vez del spinner centrado. Opcional: sin esto, `Async` se comporta
   *  exactamente como antes. */
  skeleton?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (query.isLoading) return skeleton !== undefined ? <>{skeleton}</> : <Loading />;
  if (query.error) return <ErrorState error={query.error} retry={query.refetch} />;
  if (query.data === undefined) return null;
  const emptyNode = empty?.(query.data);
  if (emptyNode) return <>{emptyNode}</>;
  return <>{children(query.data)}</>;
}

/* ── Filas clave/valor ────────────────────────────────────────────────────── */

/**
 * Una fila «etiqueta … valor» de las carcasas móviles (portal, personal):
 * el mismo par que cada pantalla venía escribiendo a mano con className
 * `mrow`/`mrow__l`/`mrow__r` — acá queda con nombre, para no seguir
 * copiando la estructura (Fase 2, auditoría 1-sep-2026: «.dl vs .mrow, la
 * misma superficie con dos nombres»).
 *
 * Salida IDÉNTICA a la que ya había: mismas clases, mismo DOM. `.mrow` en
 * `base.css` ya resuelve el borde del último renglón vía `:last-child`, así
 * que no hace falta pasar nada especial para la última fila de una lista.
 */
export function KeyValueRow({ label, children, style }: {
  label: ReactNode; children: ReactNode; style?: React.CSSProperties;
}) {
  return (
    <div className="mrow" style={style}>
      <span className="mrow__l">{label}</span>
      <span className="mrow__r">{children}</span>
    </div>
  );
}

/* ── Tablas ───────────────────────────────────────────────────────────────── */

export function Table({ head, children, foot }: {
  head: ReactNode; children: ReactNode; foot?: ReactNode;
}) {
  return (
    <div className="table-wrap">
      <table className="tbl">
        <thead>{head}</thead>
        <tbody>{children}</tbody>
        {foot && <tfoot>{foot}</tfoot>}
      </table>
    </div>
  );
}

/** Una celda con su dato principal y una línea de contexto debajo. */
export function Cell({ children, sub }: { children: ReactNode; sub?: ReactNode }) {
  return (
    <>
      {children}
      {sub && <span className="tbl__sub">{sub}</span>}
    </>
  );
}

/* ── Formularios ──────────────────────────────────────────────────────────── */

export function Field({ label, hint, error, children, required }: {
  label: string; hint?: ReactNode; error?: string; children: ReactNode; required?: boolean;
}) {
  return (
    <div className="field">
      <label className="label">
        {label}
        {required && <span style={{ color: 'var(--danger)' }}> *</span>}
      </label>
      {children}
      {error
        ? <div className="hint" style={{ color: 'var(--danger)' }}>{error}</div>
        : hint && <div className="hint">{hint}</div>}
    </div>
  );
}

/**
 * Un `<input type="password">` con un botón para revelarla mientras se
 * escribe — el ojo de `NavIcons` (`ojo` / `ojo-cerrado`).
 */
export function PasswordInput({
  value, onChange, autoComplete, ...rest
}: {
  value: string;
  onChange: (value: string) => void;
  autoComplete?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'autoComplete'>) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="password-input">
      <input
        {...rest}
        className="input"
        type={visible ? 'text' : 'password'}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        className="password-input__toggle"
        onClick={() => setVisible((v) => !v)}
        tabIndex={-1}
        aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
      >
        <Icon name={visible ? 'ojo-cerrado' : 'ojo'} size={18} />
      </button>
    </div>
  );
}

export function Button({ variant, size, block, children, ...rest }: {
  variant?: 'primary' | 'ghost' | 'danger';
  /* `.btn--sm` existía en el CSS desde siempre, pero el componente no lo podía
     pasar. Lo destaparon los botones de «−» y «+» del recortador de foto. */
  size?: 'sm' | 'lg';
  block?: boolean;
  /** React 19 pasa `ref` como una prop más a los componentes de función, pero
   *  `ButtonHTMLAttributes` no la declara. Se necesita para devolverle el foco
   *  al botón después de un modal — ver `PhotoUploader`. */
  ref?: React.Ref<HTMLButtonElement>;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const cls = [
    'btn',
    variant ? `btn--${variant}` : '',
    size ? `btn--${size}` : '',
    block ? 'btn--block' : '',
  ].filter(Boolean).join(' ');
  return <button type="button" className={cls} {...rest}>{children}</button>;
}

/* ── Modal ────────────────────────────────────────────────────────────────── */

/** Lo que puede recibir foco con Tab. `[tabindex="-1"]` queda afuera: es el
 *  título, que se enfoca por programa pero no forma parte del ciclo. */
const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    // Lo que está oculto (`display:none`, un tab inactivo) no tiene cajas.
    .filter((el) => el.getClientRects().length > 0);
}

/* Cuántos `Modal` hay abiertos a la vez: `#root` queda `inert` mientras el
   contador sea mayor que cero, y solo se levanta cuando el último cierra. Un
   booleano se pisaría entre modales anidados —el interno lo quitaría al
   desmontarse aunque el externo siga abierto—, así que es un contador, no un
   flag. `aria-modal` en la caja ya le dice a un lector de pantalla que no
   navegue afuera, pero solo en modo de navegación por foco; `inert` es la
   garantía real: saca el fondo del árbol de accesibilidad y de Tab entero,
   con o sin lector. */
let modalesAbiertos = 0;

export function Modal({ title, onClose, children, footer, wide, xwide, persistente, hoja }: {
  title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean;
  /** No cerrar al hacer clic afuera. Para lo que tiene trabajo adentro que se
   *  perdería sin manera de recuperarlo: el encuadre de una foto se tira con un
   *  clic 4 px al lado del modal. Sin la prop, se comporta como siempre. */
  persistente?: boolean;
  /** Hoja pegada al fondo en el teléfono, del alto de lo que trae, en vez de
   *  ocupar la pantalla entera. Para lo que no es una pantalla: una invitación,
   *  una confirmación corta. En escritorio no cambia nada. */
  hoja?: boolean;
  /** Para editores que necesitan respirar: 70-90% del ancho en escritorio, y
   *  el 100% del ancho disponible en móvil (ver `.modal--xwide`). */
  xwide?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  // En una ref para que el efecto de montaje no dependa de la identidad de
  // `onClose` (casi siempre es una arrow nueva en cada render).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Foco inicial y devolución. Al abrir, el foco va al primer control del
  // cuerpo (un campo, casi siempre); si el cuerpo no tiene ninguno, al
  // primero del pie; y si no hay ninguno, al título. El botón de cerrar se
  // deja para el final: enfocarlo primero invita a cerrar lo que recién se
  // abrió. Al desmontar, el foco vuelve al elemento que abrió el modal — sin
  // esto quedaba en `<body>` y quien navega con teclado o lector volvía al
  // principio de la página.
  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const el = box.current;
    if (el) {
      const body = el.querySelector<HTMLElement>('.card__body');
      const foot = el.querySelector<HTMLElement>('.card__foot');
      const first = (body && (body.querySelector<HTMLElement>('[autofocus]') ?? focusables(body)[0]))
        ?? (foot && focusables(foot)[0]);
      // `preventScroll`: el cuerpo del modal ya está a la vista; sin esto un
      // campo largo abajo hacía scrollear el cuerpo antes de que se viera nada.
      (first ?? titleRef.current)?.focus({ preventScroll: true });
    }
    return () => {
      // Solo si sigue en el documento: si la fila que lo abrió se re-renderizó
      // y desapareció, enfocar un nodo suelto no hace nada útil.
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const root = document.getElementById('root');
    modalesAbiertos += 1;
    root?.setAttribute('inert', '');
    return () => {
      modalesAbiertos = Math.max(0, modalesAbiertos - 1);
      if (modalesAbiertos === 0) root?.removeAttribute('inert');
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    // Los eventos de un portal burbujean por el árbol de React, así que un
    // modal abierto sobre otro le pasaría su Escape y su Tab al de atrás.
    // Cada modal atiende lo suyo y corta ahí.
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCloseRef.current();
      return;
    }
    if (e.key !== 'Tab' || !box.current) return;
    // Un modal anidado (otro portal) no está dentro de esta caja en el DOM:
    // no es nuestro Tab.
    if (!(e.target instanceof Node) || !box.current.contains(e.target)) return;
    e.stopPropagation();
    const items = focusables(box.current);
    if (items.length === 0) { e.preventDefault(); return; }
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    // Tab cicla adentro: del último al primero y, con Shift, del primero al
    // último. Desde el título (foco inicial sin controles) también al primero.
    if (e.shiftKey && (active === first || active === titleRef.current)) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault(); first.focus();
    }
  }

  // A document.body, no donde lo llama la pantalla: así el CSS de impresión
  // puede aislar SOLO el modal con una regla de dos niveles, sin importar en
  // qué pantalla ni a qué profundidad se abrió. Antes imprimía la pantalla de
  // atrás entera junto con el modal, porque no había forma de distinguir «el
  // modal» de «el resto de la página» sin saber cuántos padres tenía.
  return createPortal((
    <div
      className="modal-backdrop"
      onClick={(e) => { if (!persistente && e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div
        className={`modal${xwide ? ' modal--xwide' : ''}${hoja ? ' modal--hoja' : ''}`}
        style={!xwide && wide ? { maxWidth: 760 } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={box}
        onKeyDown={onKeyDown}
      >
        <header className="card__head">
          <h2 id={titleId} ref={titleRef} tabIndex={-1}>{title}</h2>
          <div className="spacer" />
          <button type="button" className="btn btn--ghost" onClick={onClose} aria-label="Cerrar">
            <Icon name="cerrar" size={18} />
          </button>
        </header>
        <div className="card__body">{children}</div>
        {footer && <footer className="card__foot">{footer}</footer>}
      </div>
    </div>
  ), document.body);
}
